import { NavItem, NavSection, NavigationConfig } from '../types';
import { UserType } from '../../../../../types';
import { hasPermission } from '../../../../utils/permissions';
import { canSee, hasTier, isV2User } from '../../../../utils/rbac';

export function filterItemsByPermission(
  items: NavItem[],
  user: UserType | null | undefined,
  tenantId?: string
): NavItem[] {
  return items.filter((item) => {
    if (item.hidden) return false;

    // v2 role/module gate (admin-only areas, module-scoped items).
    if (!canSee(user, item.requires)) return false;

    // v2 tier gate: hide growth/enterprise-only items for users below that
    // tier. SuperKudu always bypasses; v1 users (no `user.rbac`) see the
    // item unchanged so we don't regress legacy behavior.
    if (
      item.requiresTier &&
      isV2User(user) &&
      !user?.superKudu &&
      !hasTier(user, item.requiresTier)
    ) {
      return false;
    }

    if (!item.visibleFor || item.visibleFor === 'always') {
      return true;
    }

    if (!tenantId || !user) {
      return true;
    }

    return hasPermission(user, tenantId, item.visibleFor);
  });
}

export function filterNavigationConfig(
  config: NavigationConfig,
  user: UserType | null | undefined,
  tenantId?: string
): {
  filteredSections: NavSection[];
  filteredStandaloneLinks: NavItem[];
  filteredBottomLinks: NavItem[];
} {
  const filterItems = (items: NavItem[]) =>
    filterItemsByPermission(items, user, tenantId);

  const filteredSections = config.sections
    .map((section) => ({
      ...section,
      items: filterItems(section.items),
    }))
    .filter((section) => section.items.length > 0);

  const filteredStandaloneLinks = config.standaloneLinks
    ? filterItems(config.standaloneLinks)
    : [];

  const filteredBottomLinks = config.bottomLinks
    ? filterItems(config.bottomLinks)
    : [];

  return {
    filteredSections,
    filteredStandaloneLinks,
    filteredBottomLinks,
  };
}
